import { useCallback, useEffect, useState } from 'react';

import { useApiMutation, useApiQuery } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import { ApiResponse } from '@/shared/types/apiTypes';

import {
  Categories,
  CategoryCategory,
  CategoryListResponse,
  ChildrenChildrenCategory,
  EditCategory,
  ParentCategory,
} from '../types/categoryTypes';

// API 호출 함수들
const fetchCategories = async (): Promise<
  ApiResponse<CategoryListResponse>
> => {
  return api.get<CategoryListResponse>('/api/v1/category');
};

const createCategory = async (data: {
  parentCategoryId: number;
  childCategoryName: string;
}): Promise<ApiResponse<string>> => {
  return api.post<string>(`/api/v1/category/${data.parentCategoryId}`, {
    childCategoryName: data.childCategoryName,
  });
};

const updateCategory = async (data: {
  categoryId: number;
  categoryName: string;
}): Promise<ApiResponse<string>> => {
  return api.patch<string>(`/api/v1/category/${data.categoryId}`, {
    childCategoryName: data.categoryName,
  });
};

const deleteCategory = async (
  categoryId: number,
): Promise<ApiResponse<void>> => {
  return api.delete<void>(`/api/v1/category/${categoryId}`);
};

// 상위 카테고리 목록 (하위 카테고리 생성 가능한 카테고리들)
const PARENT_CATEGORIES: ParentCategory[] = [
  { id: 2, category: 'K-MAGAZINE' },
  { id: 11, category: `SEASON'S GREETINGS` },
  { id: 12, category: 'K-CHARACTERS' },
];

export const useCategories = () => {
  const [allCategories, setAllCategories] = useState<Categories[]>([]);
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryCategory | null>(null);
  const [currentCategories, setCurrentCategories] = useState<
    ChildrenChildrenCategory[]
  >([]);
  const [editingCategory, setEditingCategory] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{
    categoryId: number;
    categoryName: string;
  }>({
    categoryId: 0,
    categoryName: '',
  });
  const [newCategory, setNewCategory] = useState({
    parentCategoryId: '2',
    childCategoryName: '',
  });

  // 카테고리 목록 조회
  const {
    data: categoryData,
    isLoading,
    refetch: refetchCategories,
  } = useApiQuery(['categories'], fetchCategories);

  // 카테고리 생성 mutation
  const createCategoryMutation = useApiMutation(createCategory, {
    onSuccess: () => {
      setNewCategory({
        parentCategoryId: selectedCategory?.id.toString() ?? '2',
        childCategoryName: '',
      });
      refetchCategories();
      alert('Category created successfully');
    },
    onError: (error: any) => {
      alert(error.message);
    },
  });

  // 카테고리 수정 mutation
  const updateCategoryMutation = useApiMutation(updateCategory, {
    onSuccess: () => {
      setEditingCategory(null);
      refetchCategories();
      alert('Category updated successfully');
    },
    onError: (error: any) => {
      alert(error.message);
    },
  });

  // 카테고리 삭제 mutation
  const deleteCategoryMutation = useApiMutation(deleteCategory, {
    onSuccess: () => {
      refetchCategories();
      alert('Category deleted successfully');
    },
    onError: (error: any) => {
      alert(error.message);
    },
  });

  // 선택된 카테고리에 따라 하위 카테고리 목록 업데이트
  const updateCurrentCategories = useCallback(
    (categoryId: number, categories: Categories[]) => {
      try {
        if (categoryId === 2) {
          // K-MAGAZINE의 하위 카테고리들
          const kMagazine = categories.find((cat) => cat.id === 2);
          setCurrentCategories(
            (kMagazine?.children as ChildrenChildrenCategory[]) || [],
          );
        } else if (categoryId === 11) {
          // SEASON'S GREETINGS의 하위 카테고리들
          const kMerch = categories.find((cat) => cat.id === 3); // K-MERCH
          const seasonsGreetings = kMerch?.children?.find(
            (child) => child.id === 11,
          );
          setCurrentCategories(
            (seasonsGreetings?.children as ChildrenChildrenCategory[]) || [],
          );
        } else if (categoryId === 12) {
          // K-CHARACTERS의 하위 카테고리들
          const kMerch = categories.find((cat) => cat.id === 3); // K-MERCH
          const kCharacters = kMerch?.children?.find(
            (child) => child.id === 12,
          );
          setCurrentCategories(
            (kCharacters?.children as ChildrenChildrenCategory[]) || [],
          );
        }
      } catch (error) {
        console.error('Error updating current categories:', error);
        setCurrentCategories([]);
      }
    },
    [],
  );

  const handleCategoryChange = useCallback(
    (category: CategoryCategory) => {
      setSelectedCategory(category);
      setEditingCategory(null);

      if (allCategories.length > 0) {
        updateCurrentCategories(category.id, allCategories);
      }
    },
    [allCategories, updateCurrentCategories],
  );

  const handleEditClick = useCallback((editCategoryData: EditCategory) => {
    setEditingCategory(editCategoryData.categoryId);
    setEditForm({
      categoryId: editCategoryData.categoryId,
      categoryName: editCategoryData.categoryName,
    });
  }, []);

  const handleSaveEdit = async (categoryId: number) => {
    if (!selectedCategory) return;

    if (editForm.categoryName.length > 40) {
      alert('Please enter category name less than 40 characters');
      return;
    }

    updateCategoryMutation.mutate({
      categoryId,
      categoryName: editForm.categoryName,
    });
  };

  const handleDelete = async (categoryId: number) => {
    if (!window.confirm('Are you sure you want to delete this category?'))
      return;
    if (!selectedCategory) return;

    deleteCategoryMutation.mutate(categoryId);
  };

  const handleCreateCategory = async () => {
    if (!newCategory.parentCategoryId || !newCategory.childCategoryName) {
      alert('Please fill out all fields');
      return;
    }

    if (newCategory.childCategoryName.length > 40) {
      alert('Please enter category name less than 40 characters');
      return;
    }

    createCategoryMutation.mutate({
      parentCategoryId: parseInt(newCategory.parentCategoryId, 10),
      childCategoryName: newCategory.childCategoryName,
    });
  };

  const handleNewCategoryChange = useCallback(
    (field: keyof typeof newCategory, value: string) => {
      setNewCategory((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  const handleEditFormChange = useCallback(
    (field: keyof typeof editForm, value: string) => {
      setEditForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  // 카테고리 데이터 처리 및 초기 선택
  useEffect(() => {
    if (categoryData?.success && categoryData.data) {
      setAllCategories(categoryData.data);

      // 초기 선택: K-MAGAZINE (id: 2)
      if (!selectedCategory) {
        const defaultCategory = PARENT_CATEGORIES[0];
        setSelectedCategory(defaultCategory);
        setNewCategory((prev) => ({
          ...prev,
          parentCategoryId: defaultCategory.id.toString(),
        }));
        updateCurrentCategories(defaultCategory.id, categoryData.data);
      }
    }
  }, [categoryData, selectedCategory, updateCurrentCategories]);

  // 선택된 카테고리가 변경될 때 현재 카테고리 목록 업데이트
  useEffect(() => {
    if (selectedCategory && allCategories.length > 0) {
      updateCurrentCategories(selectedCategory.id, allCategories);
    }
  }, [selectedCategory, allCategories, updateCurrentCategories]);

  return {
    states: {
      categories: PARENT_CATEGORIES,
      selectedCategory,
      currentCategories,
      editingCategory,
      editForm,
      newCategory,
      isLoading,
    },
    handlers: {
      handleCategoryChange,
      handleEditClick,
      handleSaveEdit,
      handleDelete,
      handleCreateCategory,
      handleNewCategoryChange,
      handleEditFormChange,
    },
  };
};
